import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Fade, InputAdornment, Popper } from '@mui/material'
import { CalendarDot } from '@phosphor-icons/react'
import Card from 'Components/UI/Card'
import { DatePicker } from 'Components/UI/DatePicker'
import { Row } from 'Components/UI/Flex'
import Caption from 'Components/UI/Forms/Caption'
import TimeSelect from 'Components/UI/Forms/TimeSelect'
import Text from 'Components/UI/Text'
import { DateTime } from 'luxon'

import { useOnClickOutside } from 'Hooks'

import { Container, StyledInput, Wrapper, WrapperProps } from './styles'

type Props = WrapperProps & {
  caption?: string
  danger?: boolean
  disabled?: boolean
  label?: ReactNode
  success?: boolean
  value?: DateTime
  onChange?: (value: DateTime) => void
}

const MINUTES_DEFAULT = 12 * 60

function DateTimeInput({
  caption,
  danger,
  disabled,
  label,
  success,
  value,
  onChange,
  ...rest
}: Props) {
  const [popperOpen, setPopperOpen] = useState(false)
  const [date, setDate] = useState<Date>()
  const [time, setTime] = useState<number>()

  useEffect(() => {
    if (!value) return

    setDate(value.setZone('utc', { keepLocalTime: true }).toJSDate())
    setTime(value.diff(value.startOf('day')).as('minutes'))
  }, [value])

  const inputRef = useRef(null)
  const popperRef = useRef(null)

  const handleOpenerClose = useCallback(() => {
    setPopperOpen(false)
  }, [])

  const handleOpenerOpen = useCallback(() => {
    setPopperOpen(true)
  }, [])

  const handleDatePickerChange = useCallback(
    (pickedDate: Date) => {
      onChange?.(
        DateTime.fromJSDate(pickedDate)
          .startOf('day')
          .setZone('utc', { keepLocalTime: true })
          .plus({ minutes: time ?? MINUTES_DEFAULT }),
      )

      setDate(pickedDate)
    },
    [time, onChange],
  )

  const handleTimeSelectChange = useCallback(
    (minutes: number) => {
      onChange?.(
        DateTime.fromJSDate(date ?? new Date())
          .startOf('day')
          .setZone('utc', { keepLocalTime: true })
          .plus({ minutes: minutes ?? 0 }),
      )
      setTime(minutes)
    },
    [date, onChange],
  )

  const formattedValue = useMemo(() => {
    if (!date || !time) {
      return ''
    }

    return DateTime.fromJSDate(date)
      .startOf('day')
      .setZone('utc', { keepLocalTime: true })
      .plus({ minutes: time })
      .toLocaleString(DateTime.DATETIME_MED)
  }, [date, time])

  useOnClickOutside(popperRef, handleOpenerClose)

  return (
    <Wrapper {...rest}>
      <Container>
        <StyledInput
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CalendarDot />
              </InputAdornment>
            ),
          }}
          disabled={disabled}
          error={danger}
          label={label}
          ref={inputRef}
          value={formattedValue}
          onClick={handleOpenerOpen}
        />

        <Popper
          anchorEl={inputRef.current}
          open={popperOpen}
          placement="bottom"
          ref={popperRef}
          style={{
            zIndex: 10000,
          }}
          transition
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={250}>
              <Card pb={5}>
                <DatePicker value={date} onChange={handleDatePickerChange} />
                <Row alignCenter fullWidth gap={3} grow justifyCenter>
                  <Text>Time </Text>
                  <TimeSelect value={time} onChange={handleTimeSelectChange} />
                </Row>
              </Card>
            </Fade>
          )}
        </Popper>
      </Container>
      <Caption caption={caption} danger={danger} mt={2} success={success} />
    </Wrapper>
  )
}

export default DateTimeInput
